<script setup>
import { useAppStore } from '~/store/main'
import headerQuery from '~/assets/gql/header.gql'

const store = useAppStore()
const {
  getMainNav,
  getRoot,
  getRootMenu,
  getShowMenuLayer,
  getLayer,
  getLayerId,
  getIsHome,
  getHeaderHeight,
} = storeToRefs(store)

const {
  setShowMenuLayer,
  setLayer,
  setRootMenu,
  setHeaderHeight,
  setMenuConfig,
} = store

const header = ref()
const showAcademy = ref(true)
const showCategories = ref(true)
const currentPosition = ref(0)
const timeOut = ref('')
const hh = ref(0)
const oldWinScroll = ref(0)
const showBread = ref(true)
const getIsDesktop = computed(() => {
  return store.getIsDesktop
})
const isLightboxOpen = ref(false)

const { locale, t } = useI18n()
const localePath = useLocalePath()

const slug = computed(() => {
  return locale.value === 'cs' ? 'menu_cz' : 'menu_en'
})

const { result } = useQuery(headerQuery, {
  slug,
})

watch(
  () => result.value,
  (newVal) => {
    if (newVal?.menus?.nodes?.length) {
      setMenuConfig(newVal.menus.nodes)
    }
  },
  { immediate: true }
)

// Scroll Handler
const onScroll = () => {
  const winScroll = window.scrollY || document.documentElement.scrollTop
  const documentHeight = document.documentElement.scrollHeight
  const clientHeight = document.documentElement.clientHeight

  const scrolled = (winScroll / (documentHeight - clientHeight)) * 100

  const myBar = document.getElementById('myBar')
  if (myBar) {
    myBar.style.width = `${Math.min(scrolled, 100)}%`
  }

  showBread.value =
    winScroll <= Math.abs(oldWinScroll.value) &&
    winScroll + clientHeight <= documentHeight - 100

  showCategories.value =
    winScroll < Math.abs(oldWinScroll.value) && winScroll <= 50

  oldWinScroll.value = winScroll
}

// Navigation Handler
const setNavigation = (layerNum) => {
  timeOut.value = setTimeout(() => {
    setLayer(layerNum)
    if (layerNum === 0) {
      setRootMenu('studium')
    } else if (layerNum === 1) {
      setRootMenu('akademie')
    } else if (layerNum === 2) {
      setRootMenu('program')
    }
    setShowMenuLayer(true)
  }, 50)
}

const clearTime = () => {
  clearTimeout(timeOut.value)
}

// Header Height Observer
onMounted(() => {
  currentPosition.value = document.body.clientHeight
  window.addEventListener('scroll', onScroll)

  const observer = new ResizeObserver((entries) => {
    entries.forEach((entry) => {
      setHeaderHeight(entry.target.offsetHeight)
    })
  })

  if (header.value) observer.observe(header.value)

  const lightboxObserver = new MutationObserver(() => {
    isLightboxOpen.value =
      document.documentElement.classList.contains('fslightbox-open')
  })

  lightboxObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  })

  setTimeout(() => {
    hh.value = getHeaderHeight
  }, 1000)

  // Clean up
  onUnmounted(() => {
    window.removeEventListener('scroll', onScroll)
    lightboxObserver.disconnect()
  })
})

// Body Class
useHead({
  bodyAttrs: {
    class: computed(() =>
      getShowMenuLayer && !isLightboxOpen.value ? 'menu-opened' : ''
    ),
  },
})
</script>

<template>
  <header ref="header" class="header">
    <ClientOnly>
      <transition name="headerSlide">
        <div
          v-if="
            getIsHome ||
            !getIsDesktop ||
            (getIsDesktop && !getIsHome && showBread)
          "
          class="mainNav"
          :style="{
            'padding-bottom': getIsHome && getIsDesktop ? '10px' : '0px',
          }"
        >
          <div>
            <div class="mainNav__inner">
              <transition name="fadeCat">
                <div v-show="getIsDesktop || showAcademy" class="academi">
                  <NuxtLink
                    class="link link__gohome"
                    :class="{ text_minor_3: getIsDesktop }"
                    :to="localePath('index')"
                    tabindex="0"
                    role="button"
                    @click="setShowMenuLayer(false)"
                    @keyup.enter="setShowMenuLayer(false)"
                  >
                    {{ t('general.avugohome') }}
                  </NuxtLink>
                </div>
              </transition>
              <ClientOnly>
                <nav aria-label="avu">
                  <ul
                    role="menubar"
                    aria-label="menu"
                    class="navigation text_main"
                    :class="{ text_minor_3: getIsDesktop }"
                  >
                    <li>
                      <NuxtLink
                        role="menuitem"
                        aria-haspopup="true"
                        tabindex="0"
                        class="link link__nav"
                        :class="{
                          'link__nav--root': getRoot === 'studium',
                          'link__nav--studium': getRootMenu === 'studium',
                        }"
                        href="#"
                        @click.prevent="setNavigation(0)"
                        @mouseover="setNavigation(0)"
                        @mouseout="clearTime"
                      >
                        {{ t('general.study') }}
                      </NuxtLink>
                    </li>
                    <li>
                      <NuxtLink
                        role="menuitem"
                        aria-haspopup="true"
                        class="link link__nav"
                        :class="{
                          'link__nav--root': getRoot === 'akademie',
                          'link__nav--akademie': getRootMenu === 'akademie',
                        }"
                        href="#"
                        @click.prevent="setNavigation(1)"
                        @mouseover="setNavigation(1)"
                        @mouseout="clearTime"
                      >
                        {{ t('general.academy') }}
                      </NuxtLink>
                    </li>
                    <li>
                      <NuxtLink
                        role="menuitem"
                        aria-haspopup="true"
                        class="link link__nav"
                        :class="{
                          'link__nav--root': getRoot === 'program',
                          'link__nav--program': getRootMenu === 'program',
                        }"
                        href="#"
                        @click.prevent="setNavigation(2)"
                        @mouseover="setNavigation(2)"
                        @mouseout="clearTime"
                      >
                        {{ t('general.program') }}
                      </NuxtLink>
                    </li>
                  </ul>
                </nav>
              </ClientOnly>
              <div v-show="getIsDesktop" class="buttons">
                <ThemeSwitcher />
                <NuxtLink
                  class="searchBox"
                  :to="localePath('search')"
                  @click="setShowMenuLayer(false)"
                  @keyup.enter="setShowMenuLayer(false)"
                >
                  <div class="lupa">
                    <div class="lupa__inner" />
                  </div>
                  <i class="arrow left"></i>
                </NuxtLink>
                <LanguageSwitcher :nav="true" />
              </div>
            </div>

            <div
              v-show="getIsDesktop && !getIsHome && showBread"
              class="bread__line"
            ></div>
          </div>
        </div>
      </transition>
    </ClientOnly>
    <transition name="fade">
      <MenuLayer
        v-if="getMainNav?.length && getShowMenuLayer && !getIsDesktop"
        :id="getLayerId"
        :layer="getLayer"
        class="navigation__layer"
      />
    </transition>
    <transition name="fade">
      <MenuLayerDesktop
        v-if="getMainNav?.length && getShowMenuLayer && getIsDesktop"
        :id="getLayerId"
        :layer="getLayer"
        class="navigation__layer"
      />
    </transition>
    <ClientOnly>
      <transition name="academiFade">
        <div v-if="getIsDesktop && !getIsHome" class="bread">
          <BreadCrumber class="bread__cumber" />
        </div>
      </transition>
    </ClientOnly>
    <div class="indicator">
      <div id="myBar" class="indicator__bar"></div>
    </div>
    <ClientOnly>
      <div v-if="getIsDesktop">
        <transition name="mainCategoriesSlide">
          <div v-show="getIsHome && showCategories" class="categoriesWrapper">
            <MainCategoriesDesktop class="categoriesWrapper--inner" />
          </div>
        </transition>
      </div>
    </ClientOnly>
  </header>
</template>

<style lang="scss" scoped>
.header {
  position: fixed;
  z-index: 999;
  left: 50%;
  transform: translateX(-50%);
  overflow: hidden;
  background-color: $color-1;
  transition: background-color 0.3s;
  padding: 10px 0 0 0;

  max-height: 100vh;

  width: 100%;
  max-width: 480px;

  margin: 0 auto;

  @include media-breakpoint-up(sm) {
    max-width: calc(100vw + 5px);
  }
  // @include media-breakpoint-up(md) {
  //   max-width: calc(max(calc(80vw + 1px), 1441px));
  // }

  nav {
    padding: 9px 20px 18px 20px;

    @include media-breakpoint-up(sm) {
      flex: 1 1 100%;
      display: flex;
      flex-flow: row nowrap;
      justify-content: center;
      align-items: center;
      gap: 50px;
      padding: 0;
    }
  }
}

.mainNav {
  opacity: 1;
  overflow: hidden;
  max-height: 500px;
  @include media-breakpoint-up(sm) {
    padding: 0px 40px 0px 40px;
    // margin-bottom: 20px;
  }
  &__inner {
    padding: 10px 0 0 0;
    @include media-breakpoint-up(sm) {
      display: flex;
      flex-flow: row nowrap;
      justify-content: space-between;
      align-items: center;
      gap: 20px;
      padding: 10px 0 0 0;
      max-width: 1800px;
      margin-left: auto;
      margin-right: auto;
    }
  }
}

.buttons {
  flex: 1 1 50%;
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  align-items: center;
  gap: 25px;
}

.searchBox {
  padding-bottom: 7px;
  &:hover {
    .lupa__inner {
      border-color: $color_7 !important;
    }
    .lupa__inner:after {
      background-color: $color_7 !important;
    }
  }

  .lupa__inner {
    width: 13px;
    height: 13px;
    border: 2px solid $color-6;
    border-radius: 50%;
    position: relative;
    margin-right: 0px;
  }

  .lupa__inner:after {
    position: absolute;
    width: 100%;
    height: 2px;
    background-color: $color-6;
    transition: background-color 0.3s;
    display: block;
    content: '';
    top: 11px;
    left: 8px;
    transform: rotate(45deg);
  }
}

.academi {
  font-size: 16px;
  letter-spacing: 0.16px;
  text-align: center;
  white-space: nowrap;
  max-height: 100px;

  @include media-breakpoint-up(sm) {
    flex: 1 1 50%;
    text-align: left;
  }
}

.navigation {
  display: flex;
  flex-flow: row wrap;
  font-size: 19px;
  justify-content: space-between;
  align-items: center;
  // height: 38px;

  @include media-breakpoint-up(sm) {
    gap: 50px;
  }

  &__layer {
    background-color: $color-1;
    transition: background-color 0.3s;
    width: 100%;
    height: 100vh;
    padding: 0 20px;
    overflow-y: scroll;
    /* Hide scrollbar for Chrome, Safari and Opera */
    &::-webkit-scrollbar {
      display: none;
    }
    /* Hide scrollbar for IE, Edge and Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    @include media-breakpoint-up(sm) {
      padding: 0 40px;
    }
    @include media-breakpoint-up(lg) {
      padding: 0;
      max-width: 1800px;
      margin-left: auto;
      margin-right: auto;
    }
  }

  @media (min-width: 1024px) {
    font-size: calc(max(17/1440 * 100vw, 14px));
    line-height: calc(27 / 1440 * 100vw);
  }

  @media (min-width: 1880px) {
    font-size: 20px;
    line-height: 30px;
  }
}

.bread {
  padding: 0 40px 6px 36px;

  &__line {
    background-clip: content-box;
    padding-top: 20px;
    padding-bottom: 10px;
    width: 120%;
    margin-left: -40px;
    height: 1px;
    background-color: $color-4;

    box-sizing: content-box;
  }

  &__cumber {
    padding-top: 6px;
    @media screen and (min-width: 1800px) {
      max-width: 1800px;
      margin-left: auto;
      margin-right: auto;
    }
  }
}

.indicator {
  width: 100%;
  height: 1px;
  margin-top: 10px;

  &__bar {
    width: 0%;
    height: 1px;
    background-color: $color-2;
  }
}

.categoriesWrapper {
  opacity: 1;
  overflow: hidden;
  max-height: 250px;

  &--inner {
    padding: 50px 40px 30px 40px;
    @include media-breakpoint-up(lg) {
      padding: 50px 0px 30px 0px;
      max-width: 1800px;
      margin-left: auto;
      margin-right: auto;
    }
  }
}

// transitions
/* --- AcademiFade --- */
.academiFade-leave-active {
  transition:
    opacity 0.5s ease,
    height 1.5s ease;
}
.academiFade-enter-active {
  transition:
    opacity 1s ease,
    height 0.5s ease;
}

.academiFade-enter-from,
.academiFade-leave-to {
  opacity: 0;
  height: 0px;
}

/* --- Fade --- */
.fade-leave-active {
  transition:
    opacity 0.5s ease,
    height 0.5s ease;
}
.fade-enter-active {
  transition:
    opacity 1s ease,
    height 1s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  height: 0px;
}

/* --- HeaderSlide (Keyframes) --- */
@keyframes headerSlide {
  0% {
    opacity: 0;
    max-height: 0;
  }
  50% {
    opacity: 0;
    max-height: 100px;
  }
  100% {
    opacity: 1;
    max-height: 100px;
  }
}

.headerSlide-enter-active {
  animation-name: headerSlide;
  animation-duration: 0.5s;
  animation-timing-function: ease-in-out;
}
.headerSlide-leave-active {
  animation-name: headerSlide;
  animation-duration: 0.5s;
  animation-timing-function: ease-in-out;
  animation-direction: reverse;
}

.headerSlide-enter-from,
.headerSlide-leave-to {
  opacity: 0;
  max-height: 0;
}

/* --- MainCategoriesSlide (Keyframes) --- */
@keyframes mainCategoriesSlide {
  0% {
    opacity: 0;
    max-height: 0;
  }
  50% {
    opacity: 0;
    max-height: 250px;
  }
  100% {
    opacity: 1;
    max-height: 250px;
  }
}

.mainCategoriesSlide-enter-active {
  animation-name: mainCategoriesSlide;
  animation-duration: 0.5s;
  animation-timing-function: ease-in-out;
}
.mainCategoriesSlide-leave-active {
  animation-name: mainCategoriesSlide;
  animation-duration: 0.5s;
  animation-timing-function: ease-in-out;
  animation-direction: reverse;
}

.mainCategoriesSlide-enter-from,
.mainCategoriesSlide-leave-to {
  opacity: 0;
  max-height: 0;
}

/* --- FadeCat --- */
.fadeCat-enter-active {
  transition:
    opacity 0.6s ease-in,
    max-height 0.5s ease;
}
.fadeCat-leave-active {
  transition:
    opacity 0.4s ease-out,
    max-height 0.5s ease;
}

.fadeCat-enter-from,
.fadeCat-leave-to {
  opacity: 0;
  max-height: 0;
}
</style>
